import React, { useState } from 'react';
import { DeviceFrame } from './components/DeviceFrame';
import { SplashScreen } from './components/SplashScreen';
import { LoginScreen } from './components/LoginScreen';
import { ForgotPasswordScreen } from './components/ForgotPasswordScreen';
import { CreateAccountScreen } from './components/CreateAccountScreen';
import { HomeScreen } from './components/HomeScreen';
import { BillsScreen } from './components/BillsScreen';
import { TransferScreen, TransferAction } from './components/TransferScreen';
import { TransferFormScreen, TransferMode, TransferSummary } from './components/TransferFormScreen';
import { TransferOtpScreen } from './components/TransferOtpScreen';
import { TransferListScreen } from './components/TransferListScreen';
import { PaymentReceiptScreen } from './components/PaymentReceiptScreen';
import { CardsScreen } from './components/CardsScreen';
import { SettingsScreen } from './components/SettingsScreen';
import { NavTarget } from './components/BottomNav';
type Screen = 'splash' | 'login' | 'forgot' | 'register' | 'home' | 'bills' | 'transfer' | 'transfer-form' | 'transfer-otp' | 'transfer-receipt' | 'transfer-list' | 'cards' | 'settings';
const STEP_META: Record<Screen, {
  step: string;
  title: string;
  desc: string;
}> = {
  splash: {
    step: 'Step 1',
    title: 'Brand Splash Screen',
    desc: 'Launch surface for users aged 30–70 across Sri Lanka. Auto-advances to Sign In after 1500ms.'
  },
  login: {
    step: 'Step 2',
    title: 'Sign In',
    desc: 'Label-forward login with 60px targets, single primary action, and biometric shortcuts.'
  },
  forgot: {
    step: 'Step 3',
    title: 'Forgot Password',
    desc: 'Two-step recovery: identify with NIC + mobile/email + account number, then set a new password.'
  },
  register: {
    step: 'Step 4',
    title: 'Create Account',
    desc: "Sectioned registration grouped per Miller's Law: personal info, contact, account, security questions."
  },
  home: {
    step: 'Step 5',
    title: 'Home Dashboard',
    desc: "Modern banker-grade home applying F-pattern scanning, Gestalt similarity & proximity, Hick's Law, and Fitts's Law. Premium balance hero, quick-send favourites, four large action tiles, an insights nudge, and recent activity."
  },
  bills: {
    step: 'Step 6',
    title: 'Pay Bills',
    desc: 'Comprehensive bill payment hub with account selection, quick actions, categorized billers, and search functionality.'
  },
  transfer: {
    step: 'Step 7',
    title: 'Money Transfer Hub',
    desc: 'Entry to all transfer flows with search, favourites, transfer methods grid, and a one-tap "Repeat" recent transfers list.'
  },
  'transfer-form': {
    step: 'Step 7a',
    title: 'Transfer — Recipient & Amount',
    desc: 'Banking-standard transfer form with horizontally scrolling source-account picker, recipient block, large amount input, and a sticky Verify CTA.'
  },
  'transfer-otp': {
    step: 'Step 7b',
    title: 'Transfer — OTP Verification',
    desc: 'Two-channel OTP (mobile/email) with summary recap, focus-aware 3-3 grouped cells, 60s resend timer.'
  },
  'transfer-receipt': {
    step: 'Step 7c',
    title: 'Payment Receipt',
    desc: 'Modern full-screen receipt after a successful transfer. Success amount hero, From → To party cards, dashed ticket-stub divider, breakdown (Amount / Fee / Total), and Share + Download actions.'
  },
  'transfer-list': {
    step: 'Step 7d',
    title: 'Schedule / History',
    desc: 'Lightweight list view for scheduled transfers and past transactions.'
  },
  cards: {
    step: 'Step 8',
    title: 'Cards',
    desc: 'Wallet-style cards screen with rich card visualisations, activation status, quick actions (Freeze, Pay, Statement, Settings), and a separate panel for cards linked from other banks.'
  },
  settings: {
    step: 'Step 9',
    title: 'Settings',
    desc: 'Profile-led settings with photo + edit affordance, grouped sections for Account, Notifications (with toggles), Preferences, and Help & Support — applying Miller-friendly chunking and consistent iconography.'
  }
};
const FRAME_LABEL: Record<Screen, string> = {
  splash: 'Screen / Splash',
  login: 'Screen / Login',
  forgot: 'Screen / Forgot Password',
  register: 'Screen / Create Account',
  home: 'Screen / Home',
  bills: 'Screen / Bills',
  transfer: 'Screen / Transfer Hub',
  'transfer-form': 'Screen / Transfer Form',
  'transfer-otp': 'Screen / Transfer OTP',
  'transfer-receipt': 'Screen / Receipt',
  'transfer-list': 'Screen / Transfer List',
  cards: 'Screen / Cards',
  settings: 'Screen / Settings'
};
const EMPTY_SUMMARY: TransferSummary = {
  mode: 'another',
  source: '',
  recipient: '',
  accountNo: '',
  amount: '',
  reference: ''
};
export function App() {
  const [screen, setScreen] = useState<Screen>('splash');
  const [replayKey, setReplayKey] = useState(0);
  const [transferMode, setTransferMode] = useState<TransferMode>('another');
  const [listMode, setListMode] = useState<'schedule' | 'history'>('history');
  const [summary, setSummary] = useState<TransferSummary>(EMPTY_SUMMARY);
  const meta = STEP_META[screen];
  const handleTransferAction = (action: TransferAction) => {
    if (action === 'own' || action === 'another') {
      setTransferMode(action);
      setScreen('transfer-form');
    } else {
      setListMode(action);
      setScreen('transfer-list');
    }
  };
  const handleNav = (t: NavTarget) => {
    if (t === 'home') setScreen('home');else if (t === 'cards') setScreen('cards');else if (t === 'settings') setScreen('settings');
    // 'accounts' and 'qr' are out of scope; ignore for now
  };
  return <div className="w-full min-h-screen flex flex-col items-center" style={{
    backgroundColor: '#F8F9FA'
  }} data-id="element-0">
      <header className="w-full max-w-5xl px-8 pt-12 pb-6" data-id="element-1">
        <div className="text-[12px] uppercase tracking-[0.12em] font-semibold" style={{
        color: '#009AC7'
      }} data-id="element-2">
          RDB Mobile Banking · Wireframe Architecture
        </div>
        <h1 className="mt-3 tracking-tight" style={{
        color: '#1A1A1A',
        fontSize: 36,
        fontWeight: 700,
        lineHeight: 1.25
      }} data-id="element-3">
          {meta.step} — {meta.title}
        </h1>
        <p className="mt-3 max-w-2xl" style={{
        color: '#404040',
        fontSize: 16,
        lineHeight: 1.75
      }} data-id="element-4">
          {meta.desc}
        </p>
      </header>
      <nav className="w-full max-w-5xl px-8 mb-6 flex gap-2 flex-wrap" data-id="element-5">
        <StepTab active={screen === 'splash'} label="01 · Splash" onClick={() => {
        setScreen('splash');
        setReplayKey(k => k + 1);
      }} data-id="element-6" />
        <StepTab active={screen === 'login'} label="02 · Sign In" onClick={() => setScreen('login')} data-id="element-7" />
        <StepTab active={screen === 'forgot'} label="03 · Forgot Password" onClick={() => setScreen('forgot')} data-id="element-8" />
        <StepTab active={screen === 'register'} label="04 · Create Account" onClick={() => setScreen('register')} data-id="element-9" />
        <StepTab active={screen === 'home'} label="05 · Home" onClick={() => setScreen('home')} data-id="element-10" />
        <StepTab active={screen === 'bills'} label="06 · Bills" onClick={() => setScreen('bills')} data-id="element-11" />
        <StepTab active={screen === 'transfer'} label="07 · Transfer" onClick={() => setScreen('transfer')} data-id="element-12" />
        <StepTab active={screen === 'transfer-form'} label="07a · Transfer Form" onClick={() => setScreen('transfer-form')} data-id="element-13" />
        <StepTab active={screen === 'transfer-otp'} label="07b · OTP" onClick={() => setScreen('transfer-otp')} data-id="element-14" />
        <StepTab active={screen === 'transfer-receipt'} label="07c · Receipt" onClick={() => setScreen('transfer-receipt')} data-id="element-15" />
        <StepTab active={screen === 'transfer-list'} label="07d · Schedule/History" onClick={() => setScreen('transfer-list')} data-id="element-16" />
        <StepTab active={screen === 'cards'} label="08 · Cards" onClick={() => setScreen('cards')} data-id="element-17" />
        <StepTab active={screen === 'settings'} label="09 · Settings" onClick={() => setScreen('settings')} data-id="element-18" />
      </nav>
      <main className="flex flex-col items-center pb-16" data-id="element-19">
        <DeviceFrame label={FRAME_LABEL[screen]} data-id="element-20">
          {screen === 'splash' && <SplashScreen key={replayKey} onContinue={() => setScreen('login')} data-id="element-21" />}
          {screen === 'login' && <LoginScreen onForgot={() => setScreen('forgot')} onCreateAccount={() => setScreen('register')} onSignIn={() => setScreen('home')} data-id="element-22" />}
          {screen === 'forgot' && <ForgotPasswordScreen onBack={() => setScreen('login')} onComplete={() => setScreen('login')} data-id="element-23" />}
          {screen === 'register' && <CreateAccountScreen onBack={() => setScreen('login')} onComplete={() => setScreen('login')} data-id="element-24" />}
          {screen === 'home' && <HomeScreen onBillsClick={() => setScreen('bills')} onTransferClick={() => setScreen('transfer')} onNavigate={handleNav} data-id="element-25" />}
          {screen === 'bills' && <BillsScreen onBack={() => setScreen('home')} data-id="element-26" />}
          {screen === 'transfer' && <TransferScreen onBack={() => setScreen('home')} onSelect={handleTransferAction} data-id="element-27" />}
          {screen === 'transfer-form' && <TransferFormScreen mode={transferMode} onBack={() => setScreen('transfer')} onVerify={s => {
          setSummary(s);
          setScreen('transfer-otp');
        }} data-id="element-28" />}
          {screen === 'transfer-otp' && <TransferOtpScreen summary={summary} onBack={() => setScreen('transfer-form')} onComplete={() => setScreen('transfer-receipt')} data-id="element-29" />}
          {screen === 'transfer-receipt' && <PaymentReceiptScreen summary={summary} onClose={() => setScreen('home')} onTransferAgain={() => setScreen('transfer')} data-id="element-30" />}
          {screen === 'transfer-list' && <TransferListScreen mode={listMode} onBack={() => setScreen('transfer')} data-id="element-31" />}
          {screen === 'cards' && <CardsScreen onNavigate={handleNav} data-id="element-32" />}
          {screen === 'settings' && <SettingsScreen onNavigate={handleNav} data-id="element-33" />}
        </DeviceFrame>
        {screen === 'home' && <Specs heading="UX Theory — Home (redesigned)" rows={[['F-pattern scanning', 'Avatar + name top-left, balance hero immediately under — eye lands on identity then funds'], ['Gestalt similarity', 'All quick-send chips share circular shape & sizing — instantly readable as one group'], ['Gestalt proximity', 'Quick-send, Quick-actions, Insight, Recent — each tightly grouped with breathing room between'], ["Hick's Law", 'Only 4 primary action tiles; secondary access via "More"'], ["Fitts's Law", '52px tinted icon wells inside 96px tiles; QR Pay still elevated as largest single target'], ['Aesthetic-Usability', 'Tinted icon wells (blue/purple/green/amber) and an insight nudge make the screen feel premium'], ['Progressive disclosure', 'Balance can be masked with the eye toggle; full transaction history is one tap away']]} data-id="element-34" />}
        {screen === 'cards' && <Specs heading="UX Decisions — Cards" rows={[['Card-first hierarchy', 'Full-bleed gradient card visual dominates above the fold — the product itself is the hero'], ['Activated status', 'Persistent green/red pill ("ACTIVATED" / "FROZEN") next to the card with a soft halo'], ['Segmented control', 'RDB Cards ↔ Other Bank Cards split — no mixing of trust models'], ['Carousel swipe', 'Snap-x scrolling between multiple cards with no nav cost'], ["Fitts's Law", 'Four 76px+ quick-action tiles (Freeze, Pay, Statement, Settings) directly below the active card'], ['Linked-banks preview', 'Compact "other bank" rows visible from the RDB tab — Gestalt similarity but reduced prominence']]} data-id="element-35" />}
        {screen === 'settings' && <Specs heading="UX Decisions — Settings" rows={[['Identity-led top', 'Brand-gradient profile card with photo, name, NIC, and camera/edit affordances on both photo and meta'], ["Miller's Law", '4 grouped sections (Account, Notifications, Preferences, Help) — each containing 3–4 rows'], ['Affordance clarity', 'Toggles for notification settings; chevrons for navigational rows — never both'], ['Coloured icon wells', 'Per-row tinted icons aid recognition while staying calm (Aesthetic-Usability)'], ['Destructive isolation', 'Sign-out button styled in red, separated below all settings — prevents accidental taps'], ['Trust footer', 'App version anchored at the bottom — common banker pattern for support reference']]} data-id="element-36" />}
        {screen === 'transfer' && <Specs heading="UX Decisions — Transfer Hub" rows={[['Search-first', 'Search bar near the top filters Recent transfers live — re-pay flow in seconds'], ['Favourites', 'Horizontally scrolling avatars with yellow star badge — banker quick-send pattern'], ['2×2 methods grid', 'Own / Another Bank / Schedule / History compacted to make room for high-frequency tasks'], ['Repeat affordance', "Every recent row shows a 'Repeat' cue — Hick's Law: re-pay is one tap, no decisions"], ['Trust signal', 'Encryption note anchored at the bottom — quiet reassurance, not noise']]} data-id="element-37" />}
        {screen === 'transfer-form' && <Specs heading="UX Decisions — Transfer Form" rows={[['Source picker', 'Horizontally scrolling account cards keep "from" selection inline'], ["Miller's Law", 'Form chunked into 4 groups: source, amount, recipient, additional details'], ['Amount affordance', 'Oversized LKR-prefixed input — confirms the most consequential field at a glance'], ['Optional clarity', 'Sender mobile is explicitly tagged "(optional)"'], ['Save recipient', 'Inline toggle only shown for another-bank transfers'], ['Sticky Verify', 'Single primary CTA pinned at the bottom; disabled until form is valid']]} data-id="element-38" />}
        {screen === 'transfer-otp' && <Specs heading="UX Decisions — OTP Verification" rows={[['Summary recap', 'Amount + recipient surfaced before users authorise — prevents typos becoming losses'], ['Two channels', 'Mobile and email toggle gives users a backup if SMS is delayed'], ['3-3 grouped cells', 'Fixed-width OTP boxes with a divider between digits 3 & 4 — banking standard, easier to read'], ['Focus-aware cell', 'Active or next-to-fill cell glows in brand blue — users always know where to type'], ['Resend gate', '60-second cooldown prevents OTP spam and reduces fraud surface'], ['Confirmation', 'Success modal repeats the amount + recipient so users have closure']]} data-id="element-39" />}
        {screen === 'transfer-receipt' && <Specs heading="UX Decisions — Payment Receipt" rows={[['Reassurance hero', 'Green success ring + big amount confirm the outcome instantly — Aesthetic-Usability'], ['Party cards', 'From and To shown as discrete cards with initials avatars — easier to verify than dense rows'], ['Ticket-stub divider', 'Dashed line with semicircle cutouts evokes a printed receipt while staying modern'], ['Cost breakdown', 'Amount / Fee / Total debited shown plainly — never hide fees from the customer'], ['Action affordances', 'Share + Download surfaced; Done as the single primary path (Hick’s Law)'], ['Audit trail', 'Reference number + timestamp shown up top — what users quote when calling support']]} data-id="element-40" />}
        {screen === 'forgot' && <Specs heading="UX Decisions — Forgot Password" rows={[['Identity triangulation', 'NIC + registered mobile/email + account number — three signals required before reset code is issued'], ['Progress clarity', 'Two-step progress dots so users know exactly where they are in the flow'], ["Hick's Law", 'One primary action per step: Send Reset Code → Update Password'], ['Confirmation pattern', 'Success modal with a single "Go to Sign In" CTA — no ambiguous secondary choices'], ['Security reassurance', 'Inline trust note explains why all three fields are required']]} data-id="element-41" />}
        {screen === 'register' && <Specs heading="UX Decisions — Create Account" rows={[["Miller's Law", 'Long form split into 4 numbered sections of 1–3 fields each'], ['Personal section', 'NIC, First & Last Name, DOB (Year / Month / Day pickers)'], ['Contact section', 'Address, Email, Registered Mobile — with explanatory hints'], ['Account section', 'RDB Account Number for binding the digital identity to the bank record'], ['Security questions', '3 selectable questions, each with its own answer field'], ['Disabled CTA', 'Create Account button stays disabled until consent checkbox is ticked'], ['Confirmation', 'Success modal routes the user back to Sign In']]} data-id="element-42" />}
        {screen === 'login' && <Specs heading="UX Decisions — Sign In" rows={[['Logo placement', 'Top-left anchor — familiar Western reading pattern, builds brand trust on first scan'], ['Persistent labels', 'Field labels remain visible above inputs — clearer for low-literacy users'], ["Fitts's Law", 'All inputs 60px, primary button 60px, eye toggle 44×44px hit area'], ["Hick's Law", 'One primary path: Sign In Securely. Biometric is a single alternate'], ['Trust signals', 'Central Bank protection badge persistently visible in the footer'], ['Trilingual greeting', 'Welcome message swaps between English / Sinhala / Tamil']]} data-id="element-43" />}
        {screen === 'splash' && <div className="mt-8" data-id="element-44">
            <button onClick={() => setReplayKey(k => k + 1)} className="inline-flex items-center justify-center px-6 rounded-full font-semibold text-white" style={{
          backgroundColor: '#009AC7',
          minHeight: 56,
          fontSize: 16
        }} data-id="element-45">
              Replay Splash
            </button>
          </div>}
      </main>
    </div>;
}
function StepTab({
  active,
  label,
  onClick
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="px-5 rounded-full transition-all" style={{
    minHeight: 44,
    backgroundColor: active ? '#1A1A1A' : '#FFFFFF',
    color: active ? '#FFFFFF' : '#1A1A1A',
    border: active ? '1px solid #1A1A1A' : '1px solid #C0C0C0',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.04em'
  }} data-id="element-46">
      {label}
    </button>;
}
function Specs({
  heading,
  rows
}: {
  heading: string;
  rows: [string, string][];
}) {
  return <section className="mt-12 w-full max-w-3xl px-8" data-id="element-47">
      <h2 className="tracking-tight" style={{
      color: '#1A1A1A',
      fontSize: 20,
      fontWeight: 700
    }} data-id="element-48">
        {heading}
      </h2>
      <div className="mt-4 grid gap-3" data-id="element-49">
        {rows.map(([label, value]) => <div key={label} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 p-4 bg-white rounded-xl" style={{
        border: '1px solid #C0C0C0'
      }} data-id="element-50">
            <div className="sm:w-56 shrink-0 font-semibold" style={{
          color: '#1A1A1A',
          fontSize: 14
        }} data-id="element-51">
              {label}
            </div>
            <div style={{
          color: '#404040',
          fontSize: 14,
          lineHeight: 1.7
        }} data-id="element-52">
              {value}
            </div>
          </div>)}
      </div>
    </section>;
}